// Shared imports
const nameUtils = require('shared/name-utils')
const sharedUtils = require('shared/utils')
// Client V1 imports
const airbrake = require('client/lib/airbrake')
const businessEvents = require('client/lib/business-events')
const { send: ajax } = require('client/lib/request')
// Client V2 common imports
const userModelActions = require('client-v2/common/models/user/actions')
const routerActions = require('client-v2/common/state/router/actions')
const { FetchStateEnum } = require('client-v2/common/utils/enums')

// Action types

const SEARCH_FORM_RESET = 'SEARCH_FORM_RESET'
const SEARCH_FORM_SET_VALUE = 'SEARCH_FORM_SET_VALUE'
const SEARCH_FORM_SHOW = 'SEARCH_FORM_SHOW'
const SEARCH_FORM_GOOGLE_SUGGESTIONS_FAILED = 'SEARCH_FORM_GOOGLE_SUGGESTIONS_FAILED'
const SEARCH_FORM_GOOGLE_SUGGESTIONS_LOADED = 'SEARCH_FORM_GOOGLE_SUGGESTIONS_LOADED'
const SEARCH_FORM_GOOGLE_SUGGESTIONS_LOADING = 'SEARCH_FORM_GOOGLE_SUGGESTIONS_LOADING'
const SEARCH_FORM_SUPERSEARCH_SUGGESTIONS_FAILED = 'SEARCH_FORM_SUPERSEARCH_SUGGESTIONS_FAILED'
const SEARCH_FORM_SUPERSEARCH_SUGGESTIONS_LOADED = 'SEARCH_FORM_SUPERSEARCH_SUGGESTIONS_LOADED'
const SEARCH_FORM_SUPERSEARCH_SUGGESTIONS_LOADING = 'SEARCH_FORM_SUPERSEARCH_SUGGESTIONS_LOADING'
const SEARCH_FORM_USER_SUGGESTIONS_FAILED = 'SEARCH_FORM_USER_SUGGESTIONS_FAILED'
const SEARCH_FORM_USER_SUGGESTIONS_LOADED = 'SEARCH_FORM_USER_SUGGESTIONS_LOADED'
const SEARCH_FORM_USER_SUGGESTIONS_LOADING = 'SEARCH_FORM_USER_SUGGESTIONS_LOADING'

// Action creators

function reset() {
  return {
    type: SEARCH_FORM_RESET
  }
}

function setValue(value) {
  return {
    type: SEARCH_FORM_SET_VALUE,
    payload: value
  }
}

// Action dispatchers

function fetchSuggestions(value) {
  return async function (dispatch) {
    if (!value) {
      dispatch({
        type: SEARCH_FORM_GOOGLE_SUGGESTIONS_LOADED,
        payload: []
      })
      dispatch({
        type: SEARCH_FORM_USER_SUGGESTIONS_LOADED,
        payload: []
      })
    } else {
      dispatch(fetchSupersearchSuggestions())
      dispatch(fetchGoogleSuggestions(value))
      dispatch(fetchUserSuggestions(value))
    }
  }
}

function fetchGoogleSuggestions(value) {
  return async function (dispatch) {
    dispatch({
      type: SEARCH_FORM_GOOGLE_SUGGESTIONS_LOADING
    })
    try {
      dispatch({
        type: SEARCH_FORM_GOOGLE_SUGGESTIONS_LOADED,
        payload: await getGoogleSuggestions(value)
      })
    } catch (err) {
      dispatch({
        type: SEARCH_FORM_GOOGLE_SUGGESTIONS_FAILED
      })
      throw err
    }
  }
}

function fetchUserSuggestions(value) {
  return async function (dispatch) {
    dispatch({
      type: SEARCH_FORM_USER_SUGGESTIONS_LOADING
    })
    try {
      dispatch({
        type: SEARCH_FORM_USER_SUGGESTIONS_LOADED,
        payload: await getUserSuggestions(value)
      })
    } catch (err) {
      dispatch({
        type: SEARCH_FORM_USER_SUGGESTIONS_FAILED
      })
      throw err
    }
  }
}

function fetchSupersearchSuggestions() {
  return async function (dispatch, getState) {
    const { user, searchForm } = getState()
    if (
      user.loggedIn && !user.bot && searchForm &&
      searchForm.supersearchSuggestionsState === FetchStateEnum.EMPTY
    ) {
      dispatch({
        type: SEARCH_FORM_SUPERSEARCH_SUGGESTIONS_LOADING
      })
      try {
        dispatch({
          type: SEARCH_FORM_SUPERSEARCH_SUGGESTIONS_LOADED,
          payload: await getSupersearchSuggestions()
        })
      } catch (err) {
        dispatch({
          type: SEARCH_FORM_SUPERSEARCH_SUGGESTIONS_FAILED
        })
        throw err
      }
    }
  }
}

function show() {
  return function (dispatch) {
    dispatch({ type: SEARCH_FORM_SHOW })
    dispatch(fetchSupersearchSuggestions())
  }
}

function submit(suggestion) {
  return async function (dispatch, getState) {
    const searchFormValue = getState().searchForm.value
    if (!suggestion) {
      await businessEvents.trackSegment('search-topic', {
        text: searchFormValue
      })
      dispatch(navigateToSearchResults({ query: searchFormValue }))
    } else {
      const { complete, type } = suggestion
      await businessEvents.trackSegment('typeahead-select', {
        type,
        text: complete,
        search: searchFormValue
      })
      if (type === 'contact') {
        dispatch(userModelActions.navigateToUserProfile({
          user: { username: complete }
        }))
      } else {
        dispatch(navigateToSearchResults({ query: complete }))
      }
    }
  }
}

function navigateToSearchResults({ event, query }) {
  return function (dispatch, getState) {
    const { router } = getState()
    const href = (() => {
      /**
       * Search should stay on the same tab when submitting a new search
       * @see https://app.asana.com/0/102902769366319/1173773871260940/f
       */
      switch (router.action) {
        case 'search.showQueryCollections':
          return searchCollectionsHref(query)
        case 'search.showQueryInterests':
          return searchInterestsHref(query)
        case 'search.showQueryPeople':
          return searchPeopleHref(query)
        default:
          return searchPostsHref(query)
      }
    })()
    dispatch(routerActions.navigate({ event, href }))
  }
}

// URL Helpers

function searchBaseHref(query) {
  return `/search/${encodeURIComponent(query)}`
}

function searchCollectionsHref(query) {
  return `${searchBaseHref(query)}/collections`
}

function searchDomainHref(query) {
  return `${searchBaseHref(query)}/domain`
}

function searchInterestsHref(query) {
  return `${searchBaseHref(query)}/interests`
}

function searchPeopleHref(query) {
  return `${searchBaseHref(query)}/people`
}

function searchPostsHref(query) {
  return searchBaseHref(query)
}

// Dispatcher helpers

async function getSupersearchSuggestions() {
  return ajax({
    url: '/api/v0.1/user/supersearch',
    method: 'post'
  })
  .catch(() => [])
}

async function getUserSuggestions(query) {
  const { items } = await ajax({
    url: '/api/v0.1/users/search',
    method: 'post',
    data: { text: query }
  })
  return items.slice(0, 3).map(user => ({
    complete: user.username,
    names: [ user['display_name'], `@${user.username}` ],
    photo: user['photo_url'],
    type: 'contact'
  }))
}

async function getGoogleSuggestions(query) {
  const cleanQuery = nameUtils.displayText(query)
  const querySuggestion = {
    complete: cleanQuery,
    names: [cleanQuery],
    type: 'google'
  }
  const suggestions = [querySuggestion]
  return ajax({
    url: 'https://suggestqueries.google.com/complete/search',
    method: 'get',
    dataType: 'jsonp',
    data: {
      q: query,
      client: 'chrome'
    }
  })
  .then(response => {
    response[1]
    .filter(text => !sharedUtils.isExternalUrl(text))
    .slice(0, 6)
    .forEach(text => {
      const cleanText = nameUtils.displayText(text)
      // The original query is already at the top of the list
      if (cleanText !== cleanQuery) {
        suggestions.push({
          complete: cleanText,
          names: [cleanText],
          type: 'google'
        })
      }
    })
  })
  /**
   * Gracefully handle Google suggestqueries API errors
   * @see https://app.asana.com/0/102902769366319/690838729515894/f
   */
  .catch(err => {
    airbrake.notify(err)
  })
  .then(() => suggestions)
}

module.exports = {
  // Action types
  SEARCH_FORM_RESET,
  SEARCH_FORM_SET_VALUE,
  SEARCH_FORM_SHOW,
  SEARCH_FORM_GOOGLE_SUGGESTIONS_FAILED,
  SEARCH_FORM_GOOGLE_SUGGESTIONS_LOADED,
  SEARCH_FORM_GOOGLE_SUGGESTIONS_LOADING,
  SEARCH_FORM_SUPERSEARCH_SUGGESTIONS_FAILED,
  SEARCH_FORM_SUPERSEARCH_SUGGESTIONS_LOADED,
  SEARCH_FORM_SUPERSEARCH_SUGGESTIONS_LOADING,
  SEARCH_FORM_USER_SUGGESTIONS_FAILED,
  SEARCH_FORM_USER_SUGGESTIONS_LOADED,
  SEARCH_FORM_USER_SUGGESTIONS_LOADING,
  // Action creators
  reset,
  setValue,
  // Action dispatchers
  fetchSuggestions,
  navigateToSearchResults,
  show,
  submit,
  // Helpers
  searchCollectionsHref,
  searchDomainHref,
  searchInterestsHref,
  searchPeopleHref,
  searchPostsHref
}
